/**
 * data-testid 중앙 팩토리 (S6.4) — SoT.
 *
 * 규약: kebab-case `<domain>-<surface>-<role>[-<id>]`, 전부 소문자, 정규식 `^[a-z][a-z0-9-]*$`.
 * 컴포넌트와 테스트(RTL `getByTestId` + Playwright `page.getByTestId`)가 **동일 상수/합성 함수**를
 * 참조해 리터럴 드리프트와 오타를 방지한다(E2E 전용 testid 분리 안 함 — 단일 네임스페이스).
 *
 * 정적 값은 상수, 동적 인스턴스는 도메인 식별자를 합성하는 함수(`projectRow(42)` → `"project-row-42"`).
 */

export const testIds = {
  // ── 로그인 ──
  login: {
    emailInput: "login-email-input",
    passwordInput: "login-password-input",
    submitBtn: "login-submit-btn",
    signupBtn: "login-signup-btn",
    error: "login-error",
    /** 밀려난 뒤 로그인 카드 첫 줄의 안내(`/login?reason=superseded` 류) */
    supersededNotice: "login-superseded-notice",
  },

  // ── 회원가입 ──
  signup: {
    emailInput: "signup-email-input",
    duplicateCheckBtn: "signup-duplicate-check-btn",
    passwordInput: "signup-password-input",
    passwordConfirmInput: "signup-password-confirm-input",
    nextBtn: "signup-next-btn",
    nameInput: "signup-name-input",
    phoneInput: "signup-phone-input",
    submitBtn: "signup-submit-btn",
  },

  // ── 프로젝트 테이블 ──
  project: {
    searchInput: "project-search-input",
    createBtn: "project-create-btn",
    memoSearchInput: "project-memo-search-input",
    /** 프로젝트 행 — `project-row-<projectId>` */
    row: (projectId: number | string) => `project-row-${projectId}`,
    /** 행 확장 토글 — `project-row-<projectId>-expand-btn` */
    expandBtn: (projectId: number | string) => `project-row-${projectId}-expand-btn`,
    /** 컨텍스트 메뉴 아이템 — `project-menu-item-<id>` */
    menuItem: (id: number | string) => `project-menu-item-${id}`,
  },

  // ── 시계열(time-series) 행 ──
  ts: {
    /** 시계열 행 — `ts-row-<timeProjectId>` */
    row: (timeProjectId: number | string) => `ts-row-${timeProjectId}`,
    menuBtn: (timeProjectId: number | string) => `ts-row-${timeProjectId}-menu-btn`,
    editBtn: (timeProjectId: number | string) => `ts-row-${timeProjectId}-edit-btn`,
    deleteBtn: (timeProjectId: number | string) => `ts-row-${timeProjectId}-delete-btn`,
  },

  // ── 모달 (이름 kebab-case 권장: time-series-add, time-series-edit, time-series-delete, project-create ...) ──
  modal: {
    root: (name: string) => `modal-${name}`,
    closeBtn: (name: string) => `modal-${name}-close-btn`,
    confirmBtn: (name: string) => `modal-${name}-confirm-btn`,
    /** 모달 내부 임의 역할 요소 — `modal-<name>-<role>` (role 은 kebab-case). 예: `("time-series-delete","date-input")`. */
    field: (name: string, role: string) => `modal-${name}-${role}`,
  },

  // ── 지형 고도 데이터 / 설계도 박스 ──
  stratumLayer: {
    /** 행 상태 칩 — `stratum-layer-status-<filename>` */
    statusChip: (filename: string) => `stratum-layer-status-${filename}`,
    /** 도면 행 — `stratum-layer-row-<filename>` */
    row: (filename: string) => `stratum-layer-row-${filename}`,
  },

  // ── 설계도 묶음(zip 업로드 단위) 행 ──
  drawingBundle: {
    /** 접힌 묶음 행 — `drawing-bundle-row-<name>` */
    row: (name: string) => `drawing-bundle-row-${name}`,
    /** 펼침 토글 — `drawing-bundle-toggle-<name>` */
    toggle: (name: string) => `drawing-bundle-toggle-${name}`,
    /** 묶음 행과 펼친 구성원을 담는 구획 상자 — `drawing-bundle-box-<name>` */
    box: (name: string) => `drawing-bundle-box-${name}`,
  },

  // ── 설계도 쌍(dwg + 정렬 dxf) 업로드 모달 ──
  drawingPair: {
    /**
     * 행별 정렬 dxf 선택을 실제로 여는 숨은 file input — `display:none` 이라 역할·이름으로는
     * 잡히지 않는다(그래서 앵커가 필요하다). 행과 버튼은 도면명이 들어간 접근명으로 잡는다.
     */
    alignmentInput: "drawing-pair-alignment-input",
  },

  // ── GeoLog ──
  geolog: {
    previewDimOverlay: "geolog-preview-dim-overlay",
  },

  // ── 체적 패널 (LayerDescription Volume) ──
  volume: {
    baseModeSelect: "volume-base-mode-select",
    /** 바닥면 모드 옵션 — `volume-base-mode-option-<mode>` */
    baseModeOption: (mode: string) => `volume-base-mode-option-${mode}`,
    heightInput: "volume-height-input",
    calculateBtn: "volume-calculate-btn",
    guideBtn: "volume-guide-btn",
    guideModal: "volume-guide-modal",
  },

  // ── 지도 / 3D 뷰어 ──
  map: {
    container2d: "map-2d-container",
    cesiumViewerBox: "cesium-viewer-box",
    profilePanel: "profile-panel",
    /** 툴버튼 — `tool-btn-<toolType>` (toolType 은 kebab-case) */
    toolBtn: (toolType: string) => `tool-btn-${toolType}`,
  },

  // ── 2D 영역 캡쳐 ──
  areaCapture: {
    toolBtn: "area-capture-tool-btn",
    specPanel: "area-capture-spec-panel",
    guidePanel: "area-capture-guide-panel",
    /** 드래그 대체 — 현재 화면 전체를 선택 영역으로 지정(키보드 경로) */
    wholeViewportBtn: "area-capture-whole-viewport-btn",
    captureBtn: "area-capture-capture-btn",
    cancelBtn: "area-capture-cancel-btn",
    closeBtn: "area-capture-close-btn",
    clampNotice: "area-capture-clamp-notice",
    highResNotice: "area-capture-high-res-notice",
    scaleRejection: "area-capture-scale-rejection",
    /** 배율 옵션 — `area-capture-scale-2x` */
    scaleOption: (scale: number) => `area-capture-scale-${scale}x`,
    serverExportSection: "area-capture-server-export-section",
    serverExportBtn: "area-capture-server-export-btn",
    serverCancelBtn: "area-capture-server-cancel-btn",
    serverDownloadBtn: "area-capture-server-download-btn",
    serverProgress: "area-capture-server-progress",
    serverCapNotice: "area-capture-server-cap-notice",
    serverResult: "area-capture-server-result",
    serverFailure: "area-capture-server-failure",
    serverLayerQuality: "area-capture-server-layer-quality",
    /** 서버 출력 형식 옵션 — `area-capture-server-format-jpeg` */
    serverFormatOption: (format: string) =>
      `area-capture-server-format-${format}`,
  },

  // ── 색상 팔레트 선택기 (ColorPicker) ──
  colorPicker: {
    trigger: "color-picker-trigger",
    /** 팔레트 옵션 — `color-picker-option-<color>` (color 는 kebab-case 색상명) */
    option: (color: string) => `color-picker-option-${color}`,
  },

  // ── 이슈 목록 (사이드바 AnnotationList) ──
  issueList: {
    /** 행의 "이슈로 이동" 버튼 — `issue-list-fly-to-btn-<annotationId>` */
    flyToBtn: (annotationId: number | string) =>
      `issue-list-fly-to-btn-${annotationId}`,
    /** 현재 시계열에 없는 이슈 안내 모달 */
    notInTimeSeriesModal: "issue-list-not-in-time-series-modal",
    openIssueInfoBtn: "issue-list-open-issue-info-btn",
    noticeCloseBtn: "issue-list-notice-close-btn",
  },

  layerCard: {
    /**
     * 선두 컨트롤 자리 — 스위치·스피너·체크박스가 서로 교체되는 고정폭 슬롯.
     * 폭이 상태와 무관하게 유지되는지(레이아웃 시프트 방지) 단언하는 앵커.
     */
    leadingSlot: "layer-card-leading-slot",
  },

  dsmColorLegend: {
    /** 고도 색상 그라데이션 바 — DSM 이 꺼지면 흑백으로 낮추는지 단언하는 앵커 */
    bar: "dsm-color-legend-bar",
  },

  // ── 프로젝트 데이터 폴더 트리 (kind = gis·line·bim·las·drawing) ──
  // 항목 이름은 testid 규약에 들어갈 수 없어 항목 행·항목 메뉴는 role·접근명으로 조회한다.
  folderTree: {
    /** 폴더 행(드래그 소스·드롭 대상) — `folder-tree-<kind>-folder-<id>` */
    folderRow: (kind: string, folderId: number | string) =>
      `folder-tree-${kind}-folder-${folderId}`,
    /** 폴더 메뉴 버튼 — `folder-tree-<kind>-folder-<id>-menu-btn` */
    folderMenuBtn: (kind: string, folderId: number | string) =>
      `folder-tree-${kind}-folder-${folderId}-menu-btn`,
    /** 폴더 표시 컨트롤 자리 — `folder-tree-<kind>-folder-<id>-show` */
    showControl: (kind: string, folderId: number | string) =>
      `folder-tree-${kind}-folder-${folderId}-show`,
    /** 새(루트) 폴더 버튼 — `folder-tree-<kind>-new-folder-btn` */
    newFolderBtn: (kind: string) => `folder-tree-${kind}-new-folder-btn`,
    /** 목록 끝 루트 드롭 영역 — `folder-tree-<kind>-root-zone` */
    rootZone: (kind: string) => `folder-tree-${kind}-root-zone`,
    /** 고른 항목이 있을 때만 서는 줄 — `folder-tree-<kind>-selection` */
    selectionBar: (kind: string) => `folder-tree-${kind}-selection`,
  },
} as const;

export type TestIds = typeof testIds;

// ── 기기 충돌 확인창 (login `DeviceConflictDialog`) ──

/** 기기 충돌 확인창의 모달 이름 — `testIds.modal.*(name)` 합성 인자 */
const DEVICE_CONFLICT_MODAL = "device-conflict";

/**
 * 기기 충돌 확인창 앵커 — 확인창 컴포넌트, 단위 테스트(`DeviceConflictDialog`·`HighboardCallbackPage`),
 * Playwright 하네스(`auth.setup`·`exclusiveLoginDev`)가 이 묶음을 참조한다. STANDALONE 인 monitoring-suite 는
 * 레포 모듈을 import 할 수 없어 `l1-auth.setup` 의 `DEVICE_CONFLICT_MODAL` 에 이름을 따로 적는다 — 이름을 바꾸면
 * 그쪽도 함께 고친다.
 */
export const deviceConflictTestIds = {
  root: testIds.modal.root(DEVICE_CONFLICT_MODAL),
  closeBtn: testIds.modal.closeBtn(DEVICE_CONFLICT_MODAL),
  confirmBtn: testIds.modal.confirmBtn(DEVICE_CONFLICT_MODAL),
  cancelBtn: testIds.modal.field(DEVICE_CONFLICT_MODAL, "cancel-btn"),
} as const;
